import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { CalculatorAdditionalPaymentApiResponse } from './types';
import { useIntl } from 'react-intl';
import { useGoogleChartsLoader } from '../../../../hooks/use-google-charts-loader';
import { sharedUtilsDebounce } from '../../../../utils';
import { utilsGetChartOptions } from './utils';

const DEBOUNCE_DELAY = 300;

const generateTicksArray = (years: number[]): Date[] => {
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  const ticksArray = [];
  for (let year = minYear; year <= maxYear; year += 5) {
    ticksArray.push(new Date(year, 0));
  }
  if (!ticksArray.some((date) => date.getFullYear() === maxYear)) {
    ticksArray.push(new Date(maxYear, 0));
  }
  return ticksArray;
};

const CalculatorAdditionalPaymentChart: React.FC<{
  data: CalculatorAdditionalPaymentApiResponse;
}> = ({ data }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<google.visualization.LineChart | null>(null);
  const intl = useIntl();
  const chartStatus = useGoogleChartsLoader();

  const processedData = useMemo(() => {
    return data?.years.map((item) => [
      new Date(item.year, 0),
      item.oldResidualDebt,
      item.newResidualDebt
    ]);
  }, [data]);

  const drawChart = useCallback(() => {
    try {
      if (chartStatus === 'loaded' && chartRef.current && processedData && google?.visualization) {
        const ticksArray = generateTicksArray(data.years.map((item) => item.year));
        const chartData = new google.visualization.DataTable({
          cols: [
            {
              id: 'year',
              label: intl.formatMessage({ id: 'chart.year', defaultMessage: 'Jaar' }),
              type: 'date'
            },
            {
              id: 'oldResidualDebt',
              label: intl.formatMessage({
                id: 'chart.oldResidualDebt',
                defaultMessage: 'Oude restschuld'
              }),
              type: 'number'
            },
            {
              id: 'newResidualDebt',
              label: intl.formatMessage({
                id: 'chart.newResidualDebt',
                defaultMessage: 'Nieuwe restschuld'
              }),
              type: 'number'
            }
          ],
          rows: processedData.map((row) => ({ c: row.map((v) => ({ v })) }))
        });

        const numberFormatOptions: google.visualization.NumberFormatOptions = {
          prefix: '€ ',
          groupingSymbol: intl.locale === 'nl' ? '.' : ',',
          fractionDigits: 0
        };

        // Format the 'oldResidualDebt' column (index 1)
        const oldDebtFormatter = new google.visualization.NumberFormat(numberFormatOptions);
        oldDebtFormatter.format(chartData, 1); // Apply format to column 1

        // Format the 'newResidualDebt' column (index 2)
        // Re-use the same options object
        const newDebtFormatter = new google.visualization.NumberFormat(numberFormatOptions);
        newDebtFormatter.format(chartData, 2); // Apply format to column 2

        if (!chartInstance.current) {
          chartInstance.current = new google.visualization.LineChart(chartRef.current);
        }
        chartInstance.current.draw(chartData, utilsGetChartOptions(ticksArray));
      }
    } catch (error) {
      console.error('Error rendering chart:', error);
    }
  }, [chartStatus, processedData, intl]);

  useEffect(() => {
    if (chartStatus === 'loaded') {
      drawChart();
    }
  }, [chartStatus, drawChart]);

  useEffect(() => {
    const { debounced: debouncedDrawChart, cancel } = sharedUtilsDebounce(
      drawChart,
      DEBOUNCE_DELAY
    );

    const handleResize = () => {
      if (chartStatus === 'loaded') {
        debouncedDrawChart();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancel();
    };
  }, [chartStatus, drawChart]);

  if (chartStatus === 'loading') {
    return <div>Loading...</div>;
  }

  if (chartStatus === 'error') {
    return <div>Error loading chart. Please try again later.</div>;
  }

  return (
    <div className={`min-h-[300px]`}>
      <div ref={chartRef} className={`w-100 h-[300px]`} />
    </div>
  );
};

export default CalculatorAdditionalPaymentChart;
